import { useEffect, useMemo, useState } from 'react';

import { specialDocumentRequirementsEnabled } from '@autocontrol/api-contracts';

import {
  ApiError,
  createContractorApi,
  type ContractorConfigurationRebasePreviewDto,
  type ContractorEquipmentTypeOptionDto,
  type ContractorInspectionSiteOptionDto,
  type JsonValue,
  type PrefillDto,
} from './api';
import { readContractorCapability } from './capability';
import {
  buildUpdatePrefillRequest,
  clientVisibleRequiredIssues,
  documentRequirementsFromPrefill,
  initializeDocumentEdits,
  initializeFieldEdits,
  summarizeClientVisibleRequiredIssues,
  type DocumentEditState,
  type FieldEditState,
} from './contractor-questionnaire-model';
import { DynamicQuestionnaire } from './contractor-questionnaire';
import {
  bookingStatusLabel,
  formatConfigurationDiffIdentity,
  formatContractorDateTime,
  formatContractorValidationMessage,
  isInitialEmptyConfigurationPreview,
  optionBusinessLabel,
  prefillStateLabel,
  shouldShowReuseSection,
} from './contractor-presentation';
import {
  ContractorCard,
  ContractorIntro,
  ContractorShell,
  ContractorTechnicalBlock,
} from './contractor-shell';

const CLOSED = new Set([
  'CAPABILITY_INVALID',
  'CAPABILITY_EXPIRED',
  'CAPABILITY_REVOKED',
  'CAPABILITY_CLOSED',
]);

const CLOSED_COPY: Record<string, string> = {
  CAPABILITY_INVALID: 'Ссылка недействительна. Доступ к анкете закрыт.',
  CAPABILITY_EXPIRED: 'Срок действия ссылки истёк. Доступ к анкете закрыт.',
  CAPABILITY_REVOKED: 'Доступ по ссылке отозван. Редактирование недоступно.',
  CAPABILITY_CLOSED: 'Редактирование закрыто. Повторный просмотр по этой ссылке недоступен.',
};

const PREFILL_LEAD =
  'Укажите площадку и вид техники, при необходимости примените сохранённые данные по этому осмотру, заполните поля и реквизиты документов, затем сохраните черновик или отправьте данные.';

/** OpenAPI 1.3.1: null when unbound; exact server fingerprint when bound. No synthesis. */
export function currentConfigurationFingerprintFromPrefill(prefill: PrefillDto): string | null {
  if (prefill.configuration === null || prefill.configuration === undefined) {
    return null;
  }
  return prefill.configuration.configuration_fingerprint;
}

interface RebasePreviewState {
  readonly siteId: string;
  readonly equipmentTypeId: string;
  readonly caseVersion: string;
  readonly prefillVersion: string;
  readonly currentFingerprint: string | null;
  readonly preview: ContractorConfigurationRebasePreviewDto;
}

export function App(props: { readonly apiBaseUrl: string }) {
  const api = useMemo(() => createContractorApi(props.apiBaseUrl), [props.apiBaseUrl]);
  const [prefill, setPrefill] = useState<PrefillDto | null>(null);
  const [sites, setSites] = useState<readonly ContractorInspectionSiteOptionDto[]>([]);
  const [equipmentTypes, setEquipmentTypes] = useState<readonly ContractorEquipmentTypeOptionDto[]>(
    [],
  );
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [selectedEquipmentTypeId, setSelectedEquipmentTypeId] = useState('');
  const [fieldEdits, setFieldEdits] = useState<FieldEditState>({});
  const [documentEdits, setDocumentEdits] = useState<DocumentEditState>({});
  const [fieldErrors, setFieldErrors] = useState<Map<string, string>>(new Map());
  const [documentErrors, setDocumentErrors] = useState<Map<string, string>>(new Map());
  const [attentionFields, setAttentionFields] = useState<Set<string>>(new Set());
  const [attentionDocuments, setAttentionDocuments] = useState<Set<string>>(new Set());
  const [rebasePreview, setRebasePreview] = useState<RebasePreviewState | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [refreshRequired, setRefreshRequired] = useState(false);
  const [closed, setClosed] = useState<string | null>(
    readContractorCapability() === null ? 'CAPABILITY_INVALID' : null,
  );
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (closed !== null) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const [dto, siteOptions, equipmentOptions] = await Promise.all([
          api.getPrefill(),
          api.listInspectionSites(),
          api.listEquipmentTypes(),
        ]);
        if (cancelled) {
          return;
        }
        setSites(siteOptions);
        setEquipmentTypes(equipmentOptions);
        applyDto(dto, { preserveLocalEdits: false });
        setLoadError(null);
      } catch (error) {
        if (!cancelled) {
          applyError(error);
          if (!(
            error instanceof ApiError &&
            error.problem.code !== undefined &&
            CLOSED.has(error.problem.code)
          )) {
            setLoadError('Не удалось загрузить анкету.');
          }
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, closed]);

  const applyDto = (
    dto: PrefillDto,
    options: {
      preserveLocalEdits: boolean;
      attentionFieldKeys?: readonly string[];
      attentionDocumentKeys?: readonly string[];
    } = { preserveLocalEdits: false },
  ) => {
    setPrefill(dto);
    const siteId =
      dto.selected_inspection_site?.inspection_site_id ??
      dto.configuration?.inspection_site_id ??
      '';
    const equipmentId =
      dto.selected_equipment_type?.equipment_type_id ?? dto.configuration?.equipment_type_id ?? '';
    setSelectedSiteId(siteId);
    setSelectedEquipmentTypeId(equipmentId);
    if (!options.preserveLocalEdits) {
      setFieldEdits(initializeFieldEdits(dto.resolved_form_schema, dto.data));
      setDocumentEdits(initializeDocumentEdits(documentRequirementsFromPrefill(dto), dto.data));
    }
    setConflict(false);
    setRefreshRequired(dto.configuration?.freshness === 'CONFIGURATION_REFRESH_REQUIRED');
    setFieldErrors(new Map());
    setDocumentErrors(new Map());
    setAttentionFields(new Set(options.attentionFieldKeys ?? []));
    setAttentionDocuments(new Set(options.attentionDocumentKeys ?? []));
    setRebasePreview(null);
  };

  const applyError = (error: unknown) => {
    if (
      error instanceof ApiError &&
      error.problem.code !== undefined &&
      CLOSED.has(error.problem.code)
    ) {
      setPrefill(null);
      setClosed(error.problem.code);
      return;
    }
    if (error instanceof ApiError && error.problem.code === 'STALE_VERSION') {
      setConflict(true);
      setMessage(
        'Данные на сервере изменились. Ваши правки сохранены в форме — повторите сохранение после обновления.',
      );
      return;
    }
    if (error instanceof ApiError && error.problem.code === 'CONFIGURATION_REFRESH_REQUIRED') {
      setRefreshRequired(true);
      setMessage('Требования к осмотру обновились. Перед отправкой нужно обновить анкету.');
      return;
    }
    if (
      error instanceof ApiError &&
      (error.problem.code === 'CONFIGURATION_REBASE_CONFLICT' ||
        error.problem.code === 'CONFIGURATION_INCOMPLETE')
    ) {
      setMessage(
        error.problem.code === 'CONFIGURATION_INCOMPLETE'
          ? 'Выберите площадку и тип оборудования, затем запросите предварительный просмотр конфигурации.'
          : 'Предпросмотр конфигурации устарел. Запросите новый просмотр.',
      );
      setRebasePreview(null);
      return;
    }
    if (error instanceof ApiError && error.problem.code === 'VALIDATION_ERROR') {
      const schema = prefill?.resolved_form_schema;
      const nextFieldErrors = new Map<string, string>();
      const nextDocumentErrors = new Map<string, string>();
      for (const row of error.problem.errors ?? []) {
        const mapped = formatContractorValidationMessage({
          field: row.field,
          code: row.code,
          message: row.message,
          schema,
        });
        if (row.field.includes('::')) {
          nextDocumentErrors.set(row.field.split('.')[0]!, mapped);
        } else {
          nextFieldErrors.set(row.field, mapped);
        }
      }
      setFieldErrors(nextFieldErrors);
      setDocumentErrors(nextDocumentErrors);
      setMessage(
        (error.problem.errors ?? [])
          .map((row) =>
            formatContractorValidationMessage({
              field: row.field,
              code: row.code,
              message: row.message,
              schema,
            }),
          )
          .join(' ') || 'Проверьте обязательные поля.',
      );
      return;
    }
    setMessage('Не удалось выполнить запрос.');
  };

  const invalidatePreview = () => {
    setRebasePreview(null);
  };

  const applyRebase = async (state: RebasePreviewState, successMessage: string) => {
    if (prefill === null) {
      return;
    }
    if (
      state.caseVersion !== prefill.case_version ||
      state.prefillVersion !== (prefill.prefill_version ?? '0') ||
      state.currentFingerprint !== currentConfigurationFingerprintFromPrefill(prefill)
    ) {
      setMessage('Данные изменились. Сформируйте анкету ещё раз.');
      setRebasePreview(null);
      return;
    }
    const result = await api.rebaseConfiguration({
      expected_case_version: state.caseVersion,
      expected_prefill_version: state.prefillVersion,
      current_configuration_fingerprint: state.currentFingerprint,
      target_equipment_type_id: state.equipmentTypeId,
      target_inspection_site_id: state.siteId,
      impact_checksum: state.preview.impact_checksum,
    });
    const attentionFieldKeys = [
      ...state.preview.diff.added_fields,
      ...state.preview.diff.incompatible_fields,
      ...result.unresolved_required_fields,
    ];
    const attentionDocumentKeys = [
      ...state.preview.diff.added_documents,
      ...state.preview.diff.incompatible_documents,
    ];
    applyDto(result.prefill, {
      preserveLocalEdits: false,
      attentionFieldKeys,
      attentionDocumentKeys,
    });
    setMessage(
      result.unresolved_required_fields.length > 0
        ? `${successMessage} Проверьте отмеченные поля.`
        : successMessage,
    );
  };

  const requestRebasePreview = async (siteId: string, equipmentTypeId: string) => {
    if (prefill === null) {
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const currentFingerprint = currentConfigurationFingerprintFromPrefill(prefill);
      const preview = await api.previewRebase({
        expected_case_version: prefill.case_version,
        expected_prefill_version: prefill.prefill_version ?? '0',
        current_configuration_fingerprint: currentFingerprint,
        target_equipment_type_id: equipmentTypeId,
        target_inspection_site_id: siteId,
      });
      const nextState: RebasePreviewState = {
        siteId,
        equipmentTypeId,
        caseVersion: prefill.case_version,
        prefillVersion: prefill.prefill_version ?? '0',
        currentFingerprint,
        preview,
      };
      // Initial empty bind: keep preview+apply internally as one "Сформировать анкету" action.
      if (isInitialEmptyConfigurationPreview(currentFingerprint, preview)) {
        await applyRebase(nextState, 'Анкета сформирована.');
        return;
      }
      setRebasePreview(nextState);
      setMessage('Проверьте влияние на введённые данные и подтвердите обновление анкеты.');
    } catch (error) {
      applyError(error);
    } finally {
      setBusy(false);
    }
  };

  const confirmRebase = async () => {
    if (prefill === null || rebasePreview === null) {
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      await applyRebase(rebasePreview, 'Анкета обновлена.');
    } catch (error) {
      applyError(error);
    } finally {
      setBusy(false);
    }
  };

  const cancelRebasePreview = () => {
    setRebasePreview(null);
    setMessage(null);
  };

  const selectContractorReuse = async (contractorProfileId: string) => {
    if (prefill === null || prefill.state === 'SUBMITTED') {
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const dto = await api.selectContractorReuse({
        contractor_profile_id: contractorProfileId,
        expected_case_version: prefill.case_version,
      });
      applyDto(dto, { preserveLocalEdits: false });
      setMessage('Сохранённые данные организации применены.');
    } catch (error) {
      applyError(error);
    } finally {
      setBusy(false);
    }
  };

  const selectVehicleReuse = async (vehicleProfileId: string) => {
    if (prefill === null || prefill.state === 'SUBMITTED') {
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const dto = await api.selectVehicleReuse({
        vehicle_profile_id: vehicleProfileId,
        expected_case_version: prefill.case_version,
      });
      applyDto(dto, { preserveLocalEdits: false });
      setMessage('Сохранённые данные ТС применены.');
    } catch (error) {
      applyError(error);
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (prefill === null || prefill.state === 'SUBMITTED') {
      return;
    }
    if (prefill.configuration === null || prefill.configuration === undefined) {
      setMessage('Сначала выберите площадку и тип оборудования.');
      return;
    }
    if (refreshRequired || prefill.configuration.freshness === 'CONFIGURATION_REFRESH_REQUIRED') {
      setMessage('Требования к осмотру обновились. Перед отправкой нужно обновить анкету.');
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const body = buildUpdatePrefillRequest({
        prefill,
        fieldEdits,
        documentEdits,
        inspectionSiteId: selectedSiteId || prefill.configuration.inspection_site_id,
        equipmentTypeId: selectedEquipmentTypeId || prefill.configuration.equipment_type_id,
      });
      const dto = await api.updatePrefill(body);
      applyDto(dto, { preserveLocalEdits: false });
      setMessage('Данные сохранены.');
    } catch (error) {
      applyError(error);
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (prefill === null || prefill.state === 'SUBMITTED') {
      return;
    }
    if (prefill.configuration === null || prefill.configuration === undefined) {
      setMessage('Сначала выберите площадку и тип оборудования.');
      return;
    }
    if (refreshRequired || prefill.configuration.freshness === 'CONFIGURATION_REFRESH_REQUIRED') {
      setMessage('Требования к осмотру обновились. Перед отправкой нужно обновить анкету.');
      return;
    }
    const issues = clientVisibleRequiredIssues({
      schema: prefill.resolved_form_schema,
      documents: documentRequirementsFromPrefill(prefill),
      fieldEdits,
      documentEdits,
    });
    if (issues.length > 0) {
      const summary = summarizeClientVisibleRequiredIssues({
        schema: prefill.resolved_form_schema,
        documents: documentRequirementsFromPrefill(prefill),
        fieldEdits,
        documentEdits,
      });
      setMessage(
        `Заполните обязательные сведения: полей — ${summary.fieldCount}, документов — ${summary.documentCount}.`,
      );
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const updateBody = buildUpdatePrefillRequest({
        prefill,
        fieldEdits,
        documentEdits,
        inspectionSiteId: selectedSiteId || prefill.configuration.inspection_site_id,
        equipmentTypeId: selectedEquipmentTypeId || prefill.configuration.equipment_type_id,
      });
      const saved = await api.updatePrefill(updateBody);
      const dto = await api.submitPrefill({
        expected_case_version: saved.case_version,
        expected_prefill_version: saved.prefill_version ?? '0',
        configuration_plan_revision_id: saved.configuration!.plan_revision_id,
        configuration_fingerprint: saved.configuration!.configuration_fingerprint,
      });
      applyDto(dto, { preserveLocalEdits: false });
      setMessage('Данные переданы. Анкета готова к осмотру.');
    } catch (error) {
      applyError(error);
    } finally {
      setBusy(false);
    }
  };

  const questionnaireHandlers = {
    onFieldChange: (fieldKey: string, value: JsonValue | undefined) => {
      setFieldEdits((prev) => ({ ...prev, [fieldKey]: value }));
    },
    onDocumentPresentChange: (documentKey: string, present: boolean) => {
      setDocumentEdits((prev) => ({
        ...prev,
        [documentKey]: {
          present,
          metadata: prev[documentKey]?.metadata ?? {},
        },
      }));
    },
    onDocumentMetadataChange: (
      documentKey: string,
      fieldCode: string,
      value: JsonValue | undefined,
    ) => {
      setDocumentEdits((prev) => {
        const existing = prev[documentKey] ?? { present: true, metadata: {} };
        const metadata = { ...existing.metadata };
        if (value === undefined) {
          delete metadata[fieldCode];
        } else {
          metadata[fieldCode] = value;
        }
        return {
          ...prev,
          [documentKey]: { present: true, metadata },
        };
      });
    },
  };

  if (closed !== null) {
    return (
      <main className="page page-contractor">
        <ContractorShell statusLabel="Доступ закрыт" statusTone="status--danger">
          <ContractorIntro
            eyebrow="Предзаполнение до осмотра"
            title="Подготовьте данные техники"
            lead={PREFILL_LEAD}
          />
          <p role="alert" className="banner banner-closed">
            {CLOSED_COPY[closed] ?? CLOSED_COPY.CAPABILITY_CLOSED}
          </p>
        </ContractorShell>
      </main>
    );
  }

  if (prefill === null) {
    return (
      <main className="page page-contractor">
        <ContractorShell>
          <ContractorIntro
            eyebrow="Предзаполнение до осмотра"
            title="Подготовьте данные техники"
            lead={PREFILL_LEAD}
          />
          <p role="status">{loadError ?? 'Загрузка анкеты…'}</p>
        </ContractorShell>
      </main>
    );
  }

  const readOnly = prefill.state === 'SUBMITTED';
  const configurationBound = prefill.configuration !== null && prefill.configuration !== undefined;
  const schema = prefill.resolved_form_schema;
  const documents = documentRequirementsFromPrefill(prefill);
  const reuse = prefill.reuse_context;
  const stale =
    refreshRequired || prefill.configuration?.freshness === 'CONFIGURATION_REFRESH_REQUIRED';
  const reviewSummary = summarizeClientVisibleRequiredIssues({
    schema,
    documents,
    fieldEdits,
    documentEdits,
  });
  const canMutate = configurationBound && !busy;
  const showReuse = shouldShowReuseSection(reuse);
  const bothContextSelected = selectedSiteId !== '' && selectedEquipmentTypeId !== '';
  let sectionNo = 1;
  const contextSectionTitle = `${sectionNo++}. Контекст осмотра`;
  const reuseSectionTitle = showReuse ? `${sectionNo++}. Сохранённые данные` : null;
  const fieldsSectionTitle =
    schema !== null && schema !== undefined ? `${sectionNo++}. Данные ТС / спецтехники` : null;
  const documentsEnabled = specialDocumentRequirementsEnabled();
  const documentsSectionTitle =
    documentsEnabled && schema !== null && schema !== undefined
      ? `${sectionNo++}. Документы`
      : null;
  const reviewSectionTitle = `${sectionNo++}. Проверка заполнения`;
  const contextActionLabel = configurationBound ? 'Обновить анкету' : 'Сформировать анкету';

  return (
    <main className="page page-contractor">
      <ContractorShell
        statusLabel={`Анкета: ${prefillStateLabel(prefill.state)}`}
        statusTone={prefill.state === 'SUBMITTED' ? 'status--active' : 'status--warning'}
      >
        <ContractorIntro
          eyebrow="Предзаполнение до осмотра"
          title="Подготовьте данные техники"
          lead={PREFILL_LEAD}
        />

        <section className="contractor-booking" aria-label="Сведения о записи">
          <dl className="contractor-meta-list contractor-meta-list--booking">
            <div>
              <dt>Запись на осмотр</dt>
              <dd>{bookingStatusLabel(prefill.booking.status)}</dd>
            </div>
            <div>
              <dt>Анкета</dt>
              <dd>{prefillStateLabel(prefill.state)}</dd>
            </div>
            <div>
              <dt>Дата и время осмотра</dt>
              <dd>{formatContractorDateTime(prefill.booking.scheduled_start_at)}</dd>
            </div>
            <div>
              <dt>Заполнить анкету до</dt>
              <dd>
                {formatContractorDateTime(prefill.contractor_edit_cutoff_at)}
                {readOnly ? (
                  <p className="contractor-cutoff-hint meta">Редактирование закрыто.</p>
                ) : (
                  <p className="contractor-cutoff-hint meta">
                    После начала осмотра редактирование будет недоступно.
                  </p>
                )}
              </dd>
            </div>
            {configurationBound ? (
              <div className="contractor-meta-span">
                <dt>Выбранный контекст</dt>
                <dd>
                  {prefill.configuration!.inspection_site_name} ·{' '}
                  {prefill.configuration!.equipment_type_name}
                </dd>
              </div>
            ) : null}
          </dl>
          <ContractorTechnicalBlock>
            <p>Состояние анкеты: {prefill.state}</p>
            <p>Статус записи (код): {prefill.booking.status}</p>
            {prefill.configuration !== null && prefill.configuration !== undefined ? (
              <p>Свежесть: {prefill.configuration.freshness}</p>
            ) : null}
          </ContractorTechnicalBlock>
        </section>

        {conflict ? (
          <p role="alert" className="banner banner-conflict">
            Конфликт версии. Правки не отправлены и не удалены. Обновите данные и сохраните снова.
          </p>
        ) : null}
        {stale ? (
          <div role="alert" id="freshness-banner" className="banner banner-conflict">
            <p>Требования к осмотру обновились. Перед отправкой нужно обновить анкету.</p>
          </div>
        ) : null}
        {message !== null ? (
          <p role="status" className="banner">
            {message}
          </p>
        ) : null}

        <ContractorCard
          title={contextSectionTitle}
          titleId="context-heading"
          description="Площадка и вид техники выбираются из каталога. Состав формы определяется активной конфигурацией осмотра."
        >
          <label htmlFor="inspection_site">
            Место / площадка осмотра
            <select
              id="inspection_site"
              value={selectedSiteId}
              disabled={readOnly || busy}
              onChange={(event) => {
                setSelectedSiteId(event.target.value);
                invalidatePreview();
              }}
            >
              <option value="">— Выберите площадку —</option>
              {sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {optionBusinessLabel(site.code, site.display_name)}
                </option>
              ))}
            </select>
          </label>
          <label htmlFor="equipment_type">
            Вид ТС / спецтехники
            <select
              id="equipment_type"
              value={selectedEquipmentTypeId}
              disabled={readOnly || busy}
              onChange={(event) => {
                setSelectedEquipmentTypeId(event.target.value);
                invalidatePreview();
              }}
            >
              <option value="">— Выберите вид —</option>
              {equipmentTypes.map((row) => (
                <option key={row.id} value={row.id}>
                  {optionBusinessLabel(row.code, row.display_name)}
                </option>
              ))}
            </select>
          </label>
          {!readOnly && rebasePreview === null ? (
            <button
              type="button"
              disabled={busy || !bothContextSelected}
              onClick={() => {
                void requestRebasePreview(selectedSiteId, selectedEquipmentTypeId);
              }}
            >
              {contextActionLabel}
            </button>
          ) : null}
          {configurationBound ? (
            <p className="meta">
              Текущая конфигурация: {prefill.configuration!.inspection_site_name} /{' '}
              {prefill.configuration!.equipment_type_name}.
            </p>
          ) : bothContextSelected ? (
            <p role="status" className="meta">
              Нажмите «Сформировать анкету», чтобы открыть форму предзаполнения.
            </p>
          ) : (
            <p role="status" className="meta">
              Выберите площадку и вид техники, затем сформируйте анкету.
            </p>
          )}
        </ContractorCard>

        {rebasePreview !== null ? (
          <RebasePreviewPanel
            preview={rebasePreview.preview}
            schema={schema}
            busy={busy}
            onConfirm={() => {
              void confirmRebase();
            }}
            onCancel={cancelRebasePreview}
          />
        ) : null}

        {showReuse && reuse !== undefined ? (
          <ReuseSection
            title={reuseSectionTitle!}
            reuse={reuse}
            readOnly={readOnly}
            busy={busy}
            onSelectContractor={(id) => {
              void selectContractorReuse(id);
            }}
            onSelectVehicle={(id) => {
              void selectVehicleReuse(id);
            }}
          />
        ) : null}

        {schema !== null && schema !== undefined ? (
          <>
            <ContractorCard
              title={fieldsSectionTitle!}
              titleId="prefill-fields-heading"
              description="Поля и группы берутся только из активной конфигурации осмотра."
            >
              <DynamicQuestionnaire
                schema={schema}
                documents={documents}
                fieldEdits={fieldEdits}
                documentEdits={documentEdits}
                fieldErrors={fieldErrors}
                documentErrors={documentErrors}
                readOnly={readOnly}
                busy={busy}
                attentionFieldKeys={attentionFields}
                attentionDocumentKeys={attentionDocuments}
                showDocuments={false}
                {...questionnaireHandlers}
              />
            </ContractorCard>
            {documentsSectionTitle !== null ? (
              <ContractorCard
                title={documentsSectionTitle}
                titleId="prefill-documents-heading"
                description="Состав и режим захвата определяются требованиями к осмотру. Вложения и OCR здесь недоступны."
              >
                <DynamicQuestionnaire
                  schema={schema}
                  documents={documents}
                  fieldEdits={fieldEdits}
                  documentEdits={documentEdits}
                  fieldErrors={fieldErrors}
                  documentErrors={documentErrors}
                  readOnly={readOnly}
                  busy={busy}
                  attentionFieldKeys={attentionFields}
                  attentionDocumentKeys={attentionDocuments}
                  showFields={false}
                  documentsHeading={null}
                  {...questionnaireHandlers}
                />
              </ContractorCard>
            ) : null}
          </>
        ) : configurationBound ? (
          <p role="status">Загрузка схемы анкеты…</p>
        ) : null}

        <ContractorCard
          title={reviewSectionTitle}
          titleId="prefill-review-heading"
          description="Проверьте выбранные сведения и заполненность обязательных полей перед отправкой."
        >
          {prefill.validation_errors.length > 0 ? (
            <ul className="contractor-review-list">
              {prefill.validation_errors.map((row) => (
                <li key={`${row.field}:${row.code}`}>
                  {formatContractorValidationMessage({
                    field: row.field,
                    code: row.code,
                    message: row.message,
                    schema,
                  })}
                </li>
              ))}
            </ul>
          ) : null}
          {reviewSummary.fieldCount + (documentsEnabled ? reviewSummary.documentCount : 0) > 0 ? (
            <div role="status" className="contractor-review-summary">
              <p className="meta">
                Не заполнено обязательных полей: {reviewSummary.fieldCount}
              </p>
              {documentsEnabled ? (
                <p className="meta">
                  Не заполнено обязательных документов: {reviewSummary.documentCount}
                </p>
              ) : null}
              <ul className="contractor-review-list">
                {reviewSummary.fieldLabels.map((label) => (
                  <li key={`field:${label}`}>Заполните поле «{label}».</li>
                ))}
                {documentsEnabled
                  ? reviewSummary.documentLabels.map((label) => (
                      <li key={`doc:${label}`}>Заполните реквизиты документа «{label}».</li>
                    ))
                  : null}
              </ul>
            </div>
          ) : (
            <p role="status" className="meta">
              {configurationBound
                ? 'Обязательные поля анкеты заполнены. Можно сохранить черновик или отправить данные.'
                : bothContextSelected
                  ? 'Сформируйте анкету, чтобы продолжить проверку заполнения.'
                  : 'Сначала выберите площадку и вид техники.'}
            </p>
          )}
        </ContractorCard>

        {readOnly ? (
          <p role="status" className="banner banner-success">
            Данные переданы. Анкета готова к осмотру.
          </p>
        ) : (
          <div className="contractor-actions actions">
            <div className="contractor-actions-in">
              <p className="contractor-actions-hint">
                {conflict
                  ? 'Конфликт версии — правки в форме сохранены локально'
                  : 'Черновик · изменения не отправлены до нажатия «Отправить данные»'}
              </p>
              <div className="contractor-actions-buttons">
                <button type="button" onClick={() => void save()} disabled={!canMutate}>
                  Сохранить черновик
                </button>
                <button
                  type="button"
                  className="primary"
                  onClick={() => void submit()}
                  disabled={!canMutate}
                  aria-describedby={stale ? 'freshness-banner' : undefined}
                >
                  Отправить данные
                </button>
              </div>
            </div>
          </div>
        )}
      </ContractorShell>
    </main>
  );
}

function RebasePreviewPanel(props: {
  readonly preview: ContractorConfigurationRebasePreviewDto;
  readonly schema: PrefillDto['resolved_form_schema'];
  readonly busy: boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}) {
  const { diff } = props.preview;
  const label = (identity: string) => formatConfigurationDiffIdentity(identity, props.schema);
  const rawItems = [
    ...diff.preserved_fields,
    ...diff.added_fields,
    ...diff.removed_fields,
    ...diff.incompatible_fields,
    ...diff.preserved_documents,
    ...diff.added_documents,
    ...diff.removed_documents,
    ...diff.incompatible_documents,
  ];
  return (
    <section className="rebase-preview contractor-card" aria-labelledby="rebase-preview-heading">
      <h2 id="rebase-preview-heading" className="questionnaire-section-title">
        Обновление анкеты
      </h2>
      <p className="meta">
        Будут сохранены введённые значения: {props.preview.preserved_value_count}. Потребуется
        заполнить новые обязательные поля: {props.preview.new_required_value_count}. Несовместимых
        значений: {props.preview.incompatible_value_count}.
      </p>
      <DiffList title="Будут сохранены введённые значения" items={diff.preserved_fields.map(label)} />
      <DiffList title="Добавятся поля" items={diff.added_fields.map(label)} />
      <DiffList title="Будут удалены поля" items={diff.removed_fields.map(label)} />
      <DiffList title="Потребуется заполнить заново" items={diff.incompatible_fields.map(label)} />
      <DiffList
        title="Будут сохранены документы"
        items={diff.preserved_documents.map(label)}
      />
      <DiffList title="Будут добавлены документы" items={diff.added_documents.map(label)} />
      <DiffList title="Будут удалены документы" items={diff.removed_documents.map(label)} />
      <DiffList
        title="Потребуется заполнить документы заново"
        items={diff.incompatible_documents.map(label)}
      />
      <ContractorTechnicalBlock>
        <p>Служебные идентификаторы изменений конфигурации (только для диагностики):</p>
        <ul>
          {rawItems.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </ContractorTechnicalBlock>
      <div className="actions">
        <button type="button" className="primary" disabled={props.busy} onClick={props.onConfirm}>
          Обновить анкету
        </button>
        <button type="button" disabled={props.busy} onClick={props.onCancel}>
          Отмена
        </button>
      </div>
    </section>
  );
}

function DiffList(props: { readonly title: string; readonly items: readonly string[] }) {
  if (props.items.length === 0) {
    return null;
  }
  return (
    <div className="diff-list">
      <h3>{props.title}</h3>
      <ul>
        {props.items.map((item, index) => (
          <li key={`${item}:${index}`}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

function ReuseSection(props: {
  readonly title: string;
  readonly reuse: NonNullable<PrefillDto['reuse_context']>;
  readonly readOnly: boolean;
  readonly busy: boolean;
  readonly onSelectContractor: (id: string) => void;
  readonly onSelectVehicle: (id: string) => void;
}) {
  const state = props.reuse.resolution_state;
  return (
    <ContractorCard
      title={props.title}
      titleId="reuse-heading"
      description="Только профили в контексте этого осмотра. Глобальный поиск по базе подрядчиков недоступен."
    >
      {state === 'UNRESOLVED' ? (
        <p role="status" className="meta">
          Автоматически сопоставленных сохранённых профилей нет. Заполните анкету вручную.
        </p>
      ) : null}
      {state === 'REVIEW_REQUIRED' ? (
        <p role="status" className="banner banner-conflict">
          Разрешение сохранённых данных требует проверки. Автоматический выбор недоступен. Заполните
          анкету вручную.
        </p>
      ) : null}
      {(state === 'RESOLVED_SINGLE' || state === 'RESOLVED_MULTI') &&
      props.reuse.contractor_options.length > 0 ? (
        <div className="reuse-options">
          <h3>Организация</h3>
          <ul>
            {props.reuse.contractor_options.map((option) => (
              <li key={option.contractor_profile_id}>
                <span>{option.display_name}</span>
                {!props.readOnly ? (
                  <button
                    type="button"
                    disabled={props.busy}
                    onClick={() => {
                      props.onSelectContractor(option.contractor_profile_id);
                    }}
                  >
                    Использовать сохранённые данные
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {(state === 'RESOLVED_SINGLE' || state === 'RESOLVED_MULTI') &&
      props.reuse.vehicle_options.length > 0 ? (
        <div className="reuse-options">
          <h3>ТС / оборудование</h3>
          <ul>
            {props.reuse.vehicle_options.map((option) => (
              <li key={option.vehicle_profile_id}>
                <span>{option.display_name}</span>
                {!props.readOnly ? (
                  <button
                    type="button"
                    disabled={props.busy}
                    onClick={() => {
                      props.onSelectVehicle(option.vehicle_profile_id);
                    }}
                  >
                    Использовать сохранённые данные
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </ContractorCard>
  );
}
